import { Controller } from '@hotwired/stimulus'
import AirDatepicker from 'air-datepicker'
import localeEn from 'air-datepicker/locale/en'
import {
  isValidDateOrRangeFormat,
  isSingleDate,
  getMilliseconds,
  debounce
} from "../util"

export default class extends Controller {
  static targets = ['input', 'container', 'period']
  static values = {
    defaultSelect: {
      type: Boolean,
      default: false
    },
    isRange: {
      type: Boolean,
      default: true
    },
    rangeOnly: {
      type: Boolean,
      default: false
    },
    manualInput: {
      type: Boolean,
      default: true
    },
    minDate: {
      type: String,
      default: ''
    },
    autoClose: {
      type: Boolean,
      default: false
    },
    dropdownParent: {
      type: String,
      default: ''
    }
  }

  tomSelectInstance = null
  shouldClearPeriod = false

  connect() {
    this.init()
    this.selectInitialDates()
    if (this.manualInputValue) {
      this.inputTarget.addEventListener('input', debounce(this.selectDateOnInput.bind(this), 500))
    }
    if (this.hasPeriodTarget) {
      setTimeout(() => {
        this.tomSelectInstance = this.periodTarget.tomselect
        this.tomSelectInstance.on('change', this.selectDateOnSelect.bind(this))
        if (this.defaultSelectValue && !this.inputTarget.value) {
          this.tomSelectInstance.trigger('change', this.tomSelectInstance.getValue())
        }
      }, 0)
    }
  }

  selectInitialDates() {
    const value = this.inputTarget.value
    if (!value || !isValidDateOrRangeFormat(value))
      return

    const [start, end] = value.split(' - ')

    if (isSingleDate(value))
      this.picker.selectDate(getMilliseconds(start))
    else
      this.picker.selectDate([getMilliseconds(start), getMilliseconds(end)])
  }

  disconnect() {
    if (this.picker) {
      this.picker.destroy()
      delete this.inputTarget.picker
    }
  }

  init() {
    const container = this.getContainer()
    const isBodyContainer = this.dropdownParentValue === 'body'

    this.picker = new AirDatepicker(this.inputTarget, {
      locale: {
        ...localeEn,
        daysMin: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        firstDay: 1,
      },
      navTitles: {
        days: 'MMMM yyyy',
      },
      showOtherMonths: false,
      selectOtherMonths: false,
      range: this.isRangeValue,
      toggleSelected: this.manualInputValue,
      container,
      dateFormat: 'dd/MM/yyyy',
      multipleDatesSeparator: ' - ',
      minDate: this.minDateValue,
      autoClose: this.autoCloseValue,
      onSelect: ({ date }) => {
        if (this.rangeOnlyValue && !this.isCompleteRange(date)) {
          return
        }

        this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))

        if (this.hasPeriodTarget && this.shouldClearPeriod) {
          this.tomSelectInstance.clear()
        }
        setTimeout(() => this.shouldClearPeriod = true, 0)
      },
      onHide: () => {
        if (this.rangeOnlyValue && this.picker.selectedDates.length === 1) {
          this.picker.clear()
        }
      },
      position({ $datepicker, $target }) {
        const pickerWidth = 308
        const targetRect = $target.getBoundingClientRect()

        if (isBodyContainer) {
          $datepicker.style.setProperty('position', 'fixed', 'important')
          $datepicker.style.setProperty('top', `${targetRect.bottom + 4}px`, 'important')
          $datepicker.style.setProperty('left', `${targetRect.left}px`, 'important')
          $datepicker.style.setProperty('z-index', '9999', 'important')
        } else {
          const distanceToRight = window.innerWidth - targetRect.left

          if (distanceToRight < pickerWidth) {
            $datepicker.style.left = 'auto'
            $datepicker.style.right = '0'
          } else {
            $datepicker.style.right = 'auto'
            $datepicker.style.left = '0'
          }
        }
      }
    })

    this.inputTarget.picker = this.picker
  }

  selectDateOnInput() {
    const value = this.inputTarget.value
    if (value && this.hasPeriodTarget) {
      this.tomSelectInstance.clear()
    }

    if (isValidDateOrRangeFormat(value)) {
      const [start, end] = value.split(' - ')

      isSingleDate(value)
        ? this.picker.selectDate(getMilliseconds(start))
        : this.picker.selectDate([getMilliseconds(start), getMilliseconds(end)])
      this.picker.hide()
    }

    if (!value) {
      this.picker.clear()
      this.picker.hide()
    }
  }

  selectDateOnSelect(value) {
    this.shouldClearPeriod = false
    if (isValidDateOrRangeFormat(value)) {
      let [start, end] = value.split(' - ')

      isSingleDate(value)
        ? this.picker.selectDate(getMilliseconds(start))
        : this.picker.selectDate([getMilliseconds(start), getMilliseconds(end)])
    }
  }

  isCompleteRange(date) {
    return Array.isArray(date) && date.length === 2
  }

  getContainer() {
    if (this.dropdownParentValue === 'body') {
      return document.body
    }
    return this.containerTarget
  }
}
